import React, { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import Loader from "../../Shared/Loader/Loader";
import APIConstant from "../../../utils/pathConstants";
import api from "../../../utils/apiClient";
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import UploadFile from '../../UploadFile/UploadFile';
import { apiBase } from '../../../utils/config';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const DEFAULT_RULES = [
    'Students must carry their Hall Ticket and College ID Card for entry into the examination hall.',
    'Students should take their seats 15 minutes before the scheduled start time.',
    'Late entry beyond 10 minutes after the commencement of the exam will not be permitted.',
    'Students will be allowed to leave the hall only after 1 hour from the start of the exam.',
    'Mobile phones, smart watches, and electronic gadgets are strictly prohibited inside the examination hall.',
];

const AddEditHallTicketTemplate = (props) => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState("");
    const [templateName, setTemplateName] = useState('');
    const [templateNameErr, setTemplateNameErr] = useState(false);
    const [examTitle, setExamTitle] = useState('');
    const [secondRow, setSecondRow] = useState('');
    const [thirdRow, setThirdRow] = useState('');
    const [classList, setClassList] = useState([]);
    const [className, setClassName] = useState([]);
    const [classNameErr, setClassNameErr] = useState(false);
    const [selectedYear, setSelectedYear] = useState('');
    const [examRules, setExamRules] = useState(DEFAULT_RULES);
    const [principalSign, setPrincipalSign] = useState(null);
    const [previewPrincipalSign, setPreviewPrincipalSign] = useState(null);
    const [waterMark, setWaterMark] = useState(null);
    const [previewWaterMark, setPreviewWaterMark] = useState(null);
    const [waterMarkOpacity, setWaterMarkOpacity] = useState('12');
    const signUploadRef = useRef(null);
    const waterMarkUploadRef = useRef(null);
    const titleNameRef = useRef(null);

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        GetClassList(loginData?._id, props?.masterData?.FinancialYear || props?.financialYearId);
        setTimeout(() => { titleNameRef.current?.focus(); }, 100);
        if (props?.configId) {
            setTemplateName(props?.masterData?.TemplateName || '');
            setExamTitle(props?.masterData?.ExamTitle || '');
            setSecondRow(props?.masterData?.SecondRow || '');
            setThirdRow(props?.masterData?.ThirdRow || '');
            setSelectedYear(props?.masterData?.FinancialYear);
            setClassName(props?.masterData?.ClassDetailId ? props?.masterData?.ClassDetailId.split(',') : []);
            setExamRules(Array.isArray(props?.masterData?.ExamRules) && props?.masterData?.ExamRules.length ? props?.masterData?.ExamRules : DEFAULT_RULES);
            setPrincipalSign(props?.masterData?.PrincipalSignImage ? `${apiBase}/TemplateSign/${props?.masterData?.PrincipalSignImage}` : null);
            setWaterMark(props?.masterData?.WaterMarkImage ? `${apiBase}/TemplateSign/${props?.masterData?.WaterMarkImage}` : null);
            setWaterMarkOpacity(props?.masterData?.WaterMarkOpacity ?? '12');
        } else {
            setSelectedYear(props?.financialYearId);
        }
    }, []);

    const GetClassList = async (id, academicYearId) => {
        try {
            let data = { UserID: id, FinancialYearId: academicYearId };
            await api.post(APIConstant.path.getClassList, data).then(async response => {
                if (response.success) {
                    setClassList(
                        response?.data?.filter(x => x?.Status === 'Active')?.map((x) => ({
                            ...x,
                            value: x?._id,
                            label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}-${x?.Board}`
                        }))
                    );
                    Loader.hide();
                }
            }).catch(err => { Loader.hide(); catchResponse(err); });
        } catch (err) { catchResponse(err); }
    }

    const handleRuleChange = (index, value) => {
        setExamRules(prev => prev.map((r, i) => (i === index ? value : r)));
    };
    const addRule = () => setExamRules(prev => [...prev, '']);
    const removeRule = (index) => setExamRules(prev => prev.filter((_, i) => i !== index));

    const handlePrincipalSign = (e) => {
        const selectedFile = e.files?.[0];
        if (selectedFile) {
            setPrincipalSign(selectedFile);
            setPreviewPrincipalSign(URL.createObjectURL(selectedFile));
        }
    };
    const handleDelete = () => {
        setPrincipalSign(null);
        setPreviewPrincipalSign(null);
        if (signUploadRef.current) signUploadRef.current.clear();
    };

    const handleWaterMark = (e) => {
        const selectedFile = e.files?.[0];
        if (selectedFile) {
            setWaterMark(selectedFile);
            setPreviewWaterMark(URL.createObjectURL(selectedFile));
        }
    };
    const handleWaterMarkDelete = () => {
        setWaterMark(null);
        setPreviewWaterMark(null);
        if (waterMarkUploadRef.current) waterMarkUploadRef.current.clear();
    };

    const validate = () => {
        let ok = true;
        if (CommonConfig.isEmpty(templateName)) { ok = false; setTemplateNameErr(true); } else setTemplateNameErr(false);
        if (!className.length) { ok = false; setClassNameErr(true); } else setClassNameErr(false);
        return ok;
    };

    const onCancel = () => {
        props.onCancel();
        setTemplateName('');
    }

    const AddUpdateTemplate = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const classDetailIds = [];
            const classNos = new Set();
            const mediumIds = [];
            const boardIds = [];
            (className || []).forEach((cid) => {
                const classData = classList?.find(x => x?.value === cid);
                if (classData) {
                    classDetailIds.push(classData.value);
                    if (classData.ClassId) classNos.add(classData.ClassId);
                    mediumIds.push(classData.Medium);
                    boardIds.push(classData.BoardName);
                }
            });

            const formData = new FormData();
            formData.append('id', props?.configId ?? '');
            formData.append('TemplateName', templateName);
            formData.append('ExamTitle', examTitle ?? '');
            formData.append('SecondRow', secondRow ?? '');
            formData.append('ThirdRow', thirdRow ?? '');
            formData.append('FinancialYear', selectedYear ?? '');
            formData.append('ClassId', [...classNos].join(','));
            formData.append('ClassDetailId', classDetailIds.join(','));
            formData.append('MediumId', mediumIds.join(','));
            formData.append('BoardId', boardIds.join(','));
            formData.append('ExamRules', JSON.stringify(examRules.filter(r => (r || '').trim() !== '')));
            formData.append('WaterMarkOpacity', (waterMarkOpacity ?? '').toString().trim() || '12');
            formData.append('CreatedBy', securityUserID);
            if (principalSign && principalSign.type) {
                formData.append('PrincipalSignImage', principalSign);
            } else if (CommonConfig.isEmpty(principalSign)) {
                formData.append('PrincipalSignImage', '');
            }
            if (waterMark && waterMark.type) {
                formData.append('WaterMarkImage', waterMark);
            } else if (CommonConfig.isEmpty(waterMark)) {
                formData.append('WaterMarkImage', '');
            }

            await api.post(APIConstant.path.AddUpdateHallTicketTemplate, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            }).then((response) => {
                if (response.success) {
                    Toast.success({ message: response.message });
                    Loader.hide();
                    onCancel();
                } else {
                    Toast.error({ message: response.message });
                    Loader.hide();
                }
            }).catch((err) => { Toast.error({ message: err?.message }); Loader.hide(); onCancel(); });
        } catch (err) {
            Toast.error({ message: err }); Loader.hide(); onCancel(); catchResponse(err);
        }
    };

    return (
        <div className="e-exam-master-table">
            <div style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
                <div className='p-grid'>
                    <div className="p-col-12 p-md-4 p-pt-2 custom-inputs">
                        <label htmlFor="templateName">Template Name<span style={{ color: "red" }}>*</span></label>
                        <InputText ref={titleNameRef} className='p-w-100 input-height' id="templateName" value={templateName} maxLength={100} onChange={(e) => setTemplateName(e.target.value)} />
                        <span className="error-msg" style={{ color: 'red' }}>{templateNameErr ? 'Template Name is required' : null}</span>
                    </div>
                    <div className="p-col-12 p-md-4 p-pt-2 custom-inputs">
                        <label htmlFor="examTitle">Exam Title</label>
                        <InputText className='p-w-100 input-height' id="examTitle" value={examTitle} maxLength={120} placeholder="e.g. INTERNAL EXAM (MID SEM)" onChange={(e) => setExamTitle(e.target.value)} />
                    </div>
                    <div className="p-col-12 p-md-4 p-pt-2 custom-inputs">
                        <label htmlFor="secondRow">Header Second Row</label>
                        <InputText className='p-w-100 input-height' id="secondRow" value={secondRow} maxLength={150} placeholder="Leave blank to show class & board" onChange={(e) => setSecondRow(e.target.value)} />
                    </div>
                    <div className="p-col-12 p-md-4 p-pt-2 custom-inputs">
                        <label htmlFor="thirdRow">Header Third Row</label>
                        <InputText className='p-w-100 input-height' id="thirdRow" value={thirdRow} maxLength={150} placeholder="Leave blank to show exam month/year" onChange={(e) => setThirdRow(e.target.value)} />
                    </div>
                    <div className="p-col-12 p-md-4 p-pt-2">
                        <label htmlFor="className">Class<span style={{ color: "red" }}>*</span></label>
                        <div className="custom-inputs">
                            <MultiSelect id="className" value={className} options={classList} onChange={(e) => setClassName(e.value)} placeholder="Select Class" display="chip" className='p-w-100' style={{ height: '42px' }} filter />
                            <span className="error-msg" style={{ color: 'red' }}>{classNameErr ? 'Select atleast one class' : null}</span>
                        </div>
                    </div>
                    <div className="p-col-12 p-md-4 p-pt-2">
                        <div className="custom-inputs">
                            <label htmlFor="selectedYear">Academic Years</label>
                            <Dropdown id="selectedYear" value={selectedYear} options={props?.academicYearList} onChange={(e) => setSelectedYear(e.value)} placeholder="Select Academic Year" className='p-w-100' filter optionLabel="label" optionValue="_id" showClear />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-4 p-pt-2">
                        <label>Principal Signature</label>
                        <div style={{ height: 120, width: 240 }}>
                            <UploadFile label="Sign" note="" preview={previewPrincipalSign} original={typeof principalSign === 'string' ? principalSign : null} uploadHandler={handlePrincipalSign} uploadRef={signUploadRef} handleDelete={handleDelete} isEditAllow={true} aspectRatio={NaN} />
                        </div>
                    </div>
                    <div className="p-col-12 p-md-4 p-pt-2">
                        <label>Water Mark <span style={{ fontSize: 11, color: '#888' }}>(Select 1:1 ratio image)</span></label>
                        <div style={{ height: 120, width: 240 }}>
                            <UploadFile label="Water Mark" note="" preview={previewWaterMark} original={typeof waterMark === 'string' ? waterMark : null} uploadHandler={handleWaterMark} uploadRef={waterMarkUploadRef} handleDelete={handleWaterMarkDelete} isEditAllow={true} aspectRatio={1 / 1} />
                        </div>
                        <div className="custom-inputs p-mt-4" style={{ width: 240 }}>
                            <label htmlFor="wmOpacity">Watermark Opacity (%)</label>
                            <div className="p-d-flex p-ai-center" style={{ gap: 8 }}>
                                <InputText id="wmOpacity" className='input-height' style={{ width: 90 }} value={waterMarkOpacity} keyfilter="pint" maxLength={3}
                                    onChange={(e) => {
                                        let v = (e.target.value || '').replace(/[^0-9]/g, '');
                                        if (v !== '' && Number(v) > 100) v = '100';
                                        setWaterMarkOpacity(v);
                                    }} />
                                <span style={{ fontSize: 11, color: '#888' }}>0 = invisible, 100 = solid</span>
                            </div>
                        </div>
                    </div>

                    <div className="p-col-12 p-pt-3">
                        <div className="p-d-flex p-ai-center p-jc-between">
                            <label>Exam Rules</label>
                            <Button label="Add Rule" icon="pi pi-plus" className="p-button-text p-button-sm" onClick={addRule} />
                        </div>
                        {examRules.map((rule, index) => (
                            <div key={index} className="p-d-flex p-ai-center p-mb-2" style={{ gap: 8 }}>
                                <span style={{ minWidth: 22 }}>{index + 1}.</span>
                                <InputTextarea className="p-w-100" rows={1} autoResize value={rule} onChange={(e) => handleRuleChange(index, e.target.value)} />
                                <Button icon="pi pi-times" className="p-button-text p-button-danger p-button-sm" onClick={() => removeRule(index)} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="dialog-footer p-d-flex p-ai-center p-jc-end p-pb-5">
                <Button className='p-m-0 btn-text-action-secondary p-m-1' label="Cancel" onClick={() => onCancel()} />
                <Button className='p-m-0 btn-text-action-primary submit-focus' label="Save" onClick={() => AddUpdateTemplate()} />
            </div>
        </div>
    );
};

export default AddEditHallTicketTemplate;
